import { useCheckProtectedResources } from '@/components/InvoiceReportDetailDrawer/hooks/useCheckProtectedResources';
import { useDeleteAdminInvoiceReportService } from '@/context/services/admin_invoice_reports/AdminInvoiceReportDestroy.service';
import {
  isAuthError,
  isBadRequestError,
  isInvalidError,
  isNotFoundError,
  isPaymentRequiredError,
} from '@/context/services/type';
import { invalidateAdminInvoiceReportDetailQueries } from '@/features/admin_invoice_reports/components/AdminInvoiceReportDetailDrawer/utils/invalidateDetailQueries';
import { AdminInvoiceReportColumn } from '@/features/admin_invoice_reports/types';
import { useTranslation } from '@/i18n';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTriggerFindTargetReport } from '../../List/context/TriggerFindTargetReportContext';

type UseDeleteAdminInvoiceReportProps = {
  refetch: () => Promise<unknown>;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
  selectedId: string | null;
  onCloseDeleteModal: () => void;
};

const TOAST_DURATION = 5;

export const useDeleteAdminInvoiceReport = ({
  refetch,
  onDrawerOpen,
  onDrawerClose,
  selectedId,
  onCloseDeleteModal,
}: UseDeleteAdminInvoiceReportProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { mutateAsync: deleteAdminInvoiceReport } =
    useDeleteAdminInvoiceReportService();
  const { handleCheckPermission } = useCheckProtectedResources();
  const { onTriggerFindTargetReport, onTriggerBackToPageOneIfNoData } =
    useTriggerFindTargetReport();
  const queryClient = useQueryClient();

  const onSuccessFoundNextTargetReport = useCallback(
    (report: AdminInvoiceReportColumn) => {
      onDrawerOpen(report.id);
    },
    [onDrawerOpen]
  );

  const onErrorFoundNextTargetReport = useCallback(() => {
    onDrawerClose();
  }, [onDrawerClose]);

  const invalidateDrawerIfOpen = useCallback(
    (reportId: string) => {
      if (selectedId === reportId) {
        invalidateAdminInvoiceReportDetailQueries(queryClient, reportId);
      }
    },
    [selectedId, queryClient]
  );

  const notifyDeleteError = useCallback(
    (message: string) => {
      notify.error({
        title: toHtmlNode(message),
        duration: TOAST_DURATION,
      });
    },
    [notify]
  );

  const deleteReport = useCallback(
    (reportId: string) => async () => {
      const result = await deleteAdminInvoiceReport(reportId);

      if (result.status === 'success') {
        onCloseDeleteModal();
        notify.success({
          title: t('admin_invoice_reports.delete_success'),
          duration: TOAST_DURATION,
        });
        await refetch();
        if (selectedId === reportId) {
          onTriggerFindTargetReport(
            onSuccessFoundNextTargetReport,
            onErrorFoundNextTargetReport
          );
        } else {
          onTriggerBackToPageOneIfNoData();
        }
        return;
      }

      const { errors } = result;
      const { statusCode } = errors;
      const errorMessage =
        extractErrorMessage(errors, t, 'admin_invoice_reports.error') ??
        t('admin_invoice_reports.delete_error');

      // 401 — session: reload so session middleware can redirect.
      if (isAuthError(statusCode)) {
        location.reload();
        return;
      }

      // 402 — payment required: refetch the list to reflect the changes
      if (isPaymentRequiredError(statusCode)) {
        onCloseDeleteModal();
        if (selectedId === reportId) {
          onDrawerClose();
        }
        await refetch();
        notifyDeleteError(errorMessage);
        return;
      }

      // 404 — report already deleted: close drawer if it's pointing at the
      // gone record, then refetch the list (drawer cache invalidation is moot
      // since the drawer is closed).
      if (isNotFoundError(statusCode)) {
        onCloseDeleteModal();
        if (selectedId === reportId) {
          onDrawerClose();
        }
        await refetch();
        onTriggerBackToPageOneIfNoData();
        notifyDeleteError(errorMessage);
        return;
      }

      // 400 / 422 — business-rule rejection: nothing changed server-side, so
      // no list refetch; just refresh the drawer summary in case it's stale.
      if (isInvalidError(statusCode) || isBadRequestError(statusCode)) {
        onCloseDeleteModal();
        invalidateDrawerIfOpen(reportId);
        await refetch();
        onTriggerBackToPageOneIfNoData();
        notifyDeleteError(errorMessage);
        return;
      }

      // 403 + other unknown errors — verify the user still has list access
      // before surfacing the error inline; reload if they've lost it.
      await handleCheckPermission({
        fnCheckPermission: refetch,
        onCheckSuccess: () => {
          onCloseDeleteModal();
          invalidateDrawerIfOpen(reportId);
          notifyDeleteError(errorMessage);
          onTriggerBackToPageOneIfNoData();
        },
        onCheckFailure: () => {
          location.reload();
        },
      });
    },
    [
      deleteAdminInvoiceReport,
      handleCheckPermission,
      invalidateDrawerIfOpen,
      notify,
      notifyDeleteError,
      onCloseDeleteModal,
      onDrawerClose,
      refetch,
      selectedId,
      t,
      onTriggerFindTargetReport,
      onTriggerBackToPageOneIfNoData,
      onSuccessFoundNextTargetReport,
      onErrorFoundNextTargetReport,
    ]
  );

  return {
    deleteReport,
  };
};
